import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AgentService } from '../../../api/services';
import { agentActions, clusterActions } from './agent.actions';
import { concatMap, delay, exhaustMap } from 'rxjs';
import { mapResponse } from '@ngrx/operators';
import { getErrorMessage, ZetraError } from '../../shared/errors/error-messages.helper';
import { LoadingState } from '../models/loader.model';
import { AgentClusterItem, AgentItem } from '../../../api/models';
import { ScanRange } from './agent.reducer';
// AGENT
//find all
export const agentLoadEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.loadAgentList),
      concatMap((props) =>
        agentService.agentControllerFindAllAgents(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.loadAgentListDone({ data: r.data, state: { value: LoadingState.SUCCESS }, request: props }),
            error: (error: ZetraError) =>
              agentActions.loadAgentListDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//find one
export const agentLoadOneEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.loadOne),
      concatMap((props) =>
        agentService.agentControllerGetOneAgent(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.doneOne({
                data: r.data as AgentItem & { scanRange: ScanRange },
                state: { value: LoadingState.SUCCESS },
                request: props,
              }),
            error: (error: ZetraError) =>
              agentActions.doneOne({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//create
export const agentCreateEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.createAgent),
      concatMap((props) =>
        agentService.agentControllerCreateAgent(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.createAgentDone({ data: r.data, state: { value: LoadingState.SUCCESS }, request: props }),
            error: (error: ZetraError) =>
              agentActions.createAgentDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//update
export const agentUpdateEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.updateAgent),
      concatMap((props) =>
        agentService.agentControllerUpdateAgent(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.updateAgentDone({ data: r.data, state: { value: LoadingState.SUCCESS }, request: props }),
            error: (error: ZetraError) =>
              agentActions.updateAgentDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//force delete
export const agentForceDeleteEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.forceDeleteAgent),
      concatMap((props) =>
        agentService.agentControllerDeleteAgent(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.forceDeleteAgentDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
                request: props,
              }),
            error: (error: ZetraError) =>
              agentActions.forceDeleteAgentDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

// heartbeat
export const heartbeatsLoadEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.loadHeartbeatList),
      concatMap((props) =>
        agentService.agentControllerGetHeartBeatData(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.loadHeartbeatListDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
                request: props,
              }),
            error: (error: ZetraError) =>
              agentActions.loadHeartbeatListDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

// on/off agent
export const onOffEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.onOffAgent),
      exhaustMap((props) =>
        agentService.agentControllerOnOffAgent(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.onOffAgentDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              agentActions.onOffAgentDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
// synchronize
export const synchronizeEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.synchronize),
      exhaustMap((props) =>
        agentService.agentControllerSynchronize(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.synchronizeDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              agentActions.synchronizeDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

// on/off multiple agents
export const onOffMultipleEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.onOffMultipleAgents),
      exhaustMap((props) =>
        agentService.agentControllerOnOffMultipleAgents(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.onOffMultipleAgentsDone({
                state: { value: LoadingState.SUCCESS },
                data: r.data,
              }),
            error: (error: ZetraError) =>
              agentActions.onOffMultipleAgentsDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
// synchronize multiple agents
export const synchronizeMultipleEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.synchronizeMultiple),
      exhaustMap((props) =>
        agentService.agentControllerSynchronizeMultiple(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.synchronizeMultipleDone({
                state: { value: LoadingState.SUCCESS },
                data: r.data,
              }),
            error: (error: ZetraError) =>
              agentActions.synchronizeMultipleDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//CLUSTER
//find all
export const clustersLoadEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(clusterActions.loadClusterList),
      concatMap((props) =>
        agentService.agentControllerFindAllAgentClusters(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              clusterActions.loadClusterListDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
                request: props,
              }),
            error: (error: ZetraError) =>
              clusterActions.loadClusterListDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//find one
export const clustersLoadOneEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(clusterActions.loadOneCluster),
      concatMap((props) =>
        agentService.agentControllerGetOneAgentCluster(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              clusterActions.loadOneClusterDone({
                data: {
                  ...r.data,
                  agents: r.data!.agents.sort((a, b) => a.priority! - b.priority!),
                } as AgentClusterItem,
                state: { value: LoadingState.SUCCESS },
                request: props,
              }),
            error: (error: ZetraError) =>
              clusterActions.loadOneClusterDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//create
export const clusterCreateEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(clusterActions.createCluster),
      concatMap((props) =>
        agentService.agentControllerCreateAgentCluster(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              clusterActions.createClusterDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              clusterActions.createClusterDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
//update
export const clusterUpdateEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(clusterActions.updateCluster),
      concatMap((props) =>
        agentService.agentControllerUpdateAgentCluster(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              clusterActions.updateClusterDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              clusterActions.updateClusterDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);

//agent and cluster stats
export const agentsAndClusterStatsEffect = createEffect(
  (actions$ = inject(Actions), agentService = inject(AgentService)) => {
    return actions$.pipe(
      ofType(agentActions.agentsCounterStats),
      concatMap((props) =>
        agentService.agentControllerGetAgentsAndClusterStats(props).pipe(
          delay(1000),
          mapResponse({
            next: (r) =>
              agentActions.agentsCounterStatsDone({
                data: r.data,
                state: { value: LoadingState.SUCCESS },
              }),
            error: (error: ZetraError) =>
              agentActions.agentsCounterStatsDone({
                state: {
                  value: LoadingState.FAIL,
                  message: { severity: 'warn', text: getErrorMessage(error.errorCode) },
                },
              }),
          }),
        ),
      ),
    );
  },
  { functional: true },
);
